import { useState } from 'react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Form primitives for the console.
 *
 * The vendor portal is almost entirely prose and needed very little of this; the editor
 * is nothing but forms. Kept deliberately plain — these wrap the design tokens rather
 * than introducing a component library.
 */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
      <span style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--text-strong)', fontSize: 'var(--fs-sm)' }}>
        {label}
      </span>
      {children}
      {hint && (
        <span className="vu-subtle" style={{ display: 'block', marginTop: 4, fontSize: 'var(--fs-xs)' }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function Text({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <Input
      className="vu-input"
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * A password field with a reveal toggle.
 *
 * Typing a long password blind into a form that may reject it is the single most
 * annoying thing about a sign-in screen. `type` flips rather than the input being
 * replaced, so focus and cursor position survive the toggle.
 */
export function Password({
  value,
  onChange,
  autoComplete = 'current-password',
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  id?: string;
}) {
  const [shown, setShown] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <Input
        id={id}
        className="vu-input"
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        title={shown ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)',
          display: 'grid', placeItems: 'center', width: 32, height: 32,
          cursor: 'pointer', color: 'var(--text-muted)',
          background: 'transparent', border: 0, borderRadius: 'var(--radius-sm, 6px)',
        }}
      >
        {shown ? (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <path d="M1 1l22 22" />
          </svg>
        ) : (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export function Num({
  value,
  onChange,
  step,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <Input
      className="vu-input"
      type="number"
      step={step}
      value={Number.isFinite(value) ? value : ''}
      // An empty box reads as 0 rather than NaN, which would otherwise propagate into
      // the payload and fail zod with a confusing message.
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
    />
  );
}

export function Area({
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      className="vu-input"
      rows={rows}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{ resize: 'vertical', lineHeight: 1.6 }}
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <select className="vu-input" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--fs-sm)' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{label}</span>
    </label>
  );
}

/**
 * An editable list of single-line strings — the shape behind `ul`, `ol`, `dos`, `donts`
 * and checklist items, which between them are most of the corpus.
 */
export function Lines({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const set = (i: number, v: string) => onChange(items.map((x, j) => (j === i ? v : x)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input className="vu-input" value={item} placeholder={placeholder} onChange={(e) => set(i, e.target.value)} />
          <IconBtn label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>↑</IconBtn>
          <IconBtn label="Move down" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</IconBtn>
          <IconBtn label="Remove" onClick={() => remove(i)} danger>×</IconBtn>
        </div>
      ))}
      <button type="button" className="vu-btn vu-btn-ghost" onClick={() => onChange([...items, ''])}>
        + Add item
      </button>
    </div>
  );
}

/**
 * Icon-only action with a tooltip.
 *
 * The table rows carry four actions each; as text buttons they pushed the row past the
 * width of a laptop screen. An icon needs a label to stay usable, so the tooltip is not
 * decoration here — it is what makes the icon legible. `aria-label` carries the same
 * text for screen readers and for anyone driving this by keyboard, so the meaning does
 * not depend on hover.
 */
export function IconAction({
  children,
  onClick,
  label,
  disabled,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className="vu-iconbtn"
          data-danger={danger ? 'true' : undefined}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** The 16px line icons used by the row actions. */
export const icons = {
  edit: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  // An open eye means "vendors can see this"; the struck-through eye means it is hidden.
  published: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  hidden: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  ),
  trash: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  up: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  ),
  down: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
  ),
};

export function IconBtn({
  children,
  onClick,
  label,
  disabled,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="vu-iconbtn"
      data-danger={danger ? 'true' : undefined}
    >
      {children}
    </button>
  );
}

/** Non-blocking save feedback. Errors persist; successes fade on the next action. */
export function Banner({ error, note }: { error?: string | null; note?: string | null }) {
  if (!error && !note) return null;
  return (
    <div
      role={error ? 'alert' : 'status'}
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-4)',
        fontSize: 'var(--fs-sm)',
        background: error ? 'var(--danger-bg)' : 'var(--surface-sunken)',
        color: error ? 'var(--danger)' : 'var(--text-body)',
      }}
    >
      {error ?? note}
    </div>
  );
}

/** Slugs are user-visible URLs, so derive a sane default rather than making them type it. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}
